import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { updateProfile, logout } from "../api/client";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Palette, Check, CalendarClock, Mail,
  MessageSquare, User, Lock, Save, Phone, ArrowRight,
  ShieldCheck, Star
} from "lucide-react";
import { format } from "date-fns";

const THEMES = [
  { id: "dark",  name: "Obsidian Dark",    color1: "#00f2fe", color2: "#8b5cf6" },
  { id: "light", name: "Ice Alabaster",    color1: "#0284c7", color2: "#6d28d9" }
];

// Helper — check if user's plan includes a specific support channel
function hasSupportChannel(user, channel) {
  if (!user || user.role === "admin") return true; // Admin always has full access
  const support = (user.support || user.service_plan_support || "chatbot").toLowerCase();
  return support.includes(channel.toLowerCase());
}

// Locked support item with upgrade prompt
function LockedSupportItem({ label, icon, navigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{
        position: "relative", borderRadius: "var(--radius)", overflow: "hidden",
        border: "1px solid rgba(239,68,68,0.15)", background: "rgba(239,68,68,0.03)"
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Blurred content */}
      <div style={{
        display: "flex", alignItems: "center", gap: "12px",
        padding: "12px 16px", filter: "blur(3px)", userSelect: "none",
        pointerEvents: "none"
      }}>
        {icon}
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: "13px", fontWeight: "700" }}>{label}</div>
          <div style={{ fontSize: "11px", color: "var(--text2)" }}>••••••••••••</div>
        </div>
      </div>
      {/* Overlay */}
      <div
        style={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center",
          justifyContent: "center", gap: "8px", flexDirection: "column",
          background: hovered ? "rgba(239,68,68,0.08)" : "rgba(10,12,18,0.6)",
          backdropFilter: "blur(2px)", cursor: "pointer",
          transition: "background 0.2s ease"
        }}
        onClick={() => navigate("/renew-plan")}
      >
        <Lock size={14} style={{ color: "var(--red)" }} />
        <span style={{ fontSize: "11px", fontWeight: "700", color: "var(--red)" }}>
          Upgrade to Unlock
        </span>
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, setUser, clearAuth } = useAuth();
  const navigate = useNavigate();

  const [form, setForm]       = useState({ name: user?.name || "", email: user?.email || "", password: "", confirm: "" });
  const [toast, setToast]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTheme, setActiveTheme] = useState("dark");

  useEffect(() => {
    const savedTheme = localStorage.getItem("pine_connector_theme") || "dark";
    setActiveTheme(savedTheme);
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleUpdate = async () => {
    if (form.password) {
      if (form.password !== form.confirm) {
        showToast("Passwords do not match", "error"); return;
      }
      if (!form.oldPassword) {
        showToast("Please enter your current password to authorize password changes", "error"); return;
      }
    }
    setLoading(true);
    try {
      const payload = {};
      if (form.name !== user.name)   payload.name = form.name;
      if (form.email !== user.email)  payload.email = form.email;
      if (form.password) {
        payload.password = form.password;
        payload.old_password = form.oldPassword;
      }

      const data = await updateProfile(payload);
      setUser(data.user);
      showToast("Profile updated successfully");
      setForm(p => ({ ...p, password: "", confirm: "", oldPassword: "" }));
    } catch (err) {
      showToast(err.response?.data?.detail || "Update failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await logout(); } catch {}
    clearAuth();
    navigate("/login");
  };

  const changeTheme = (themeId) => {
    const allThemeIds = ["cyberpunk", "emerald", "sunset", "amethyst", "frost", "rose", "dark", "light"];
    allThemeIds.forEach(id => document.body.classList.remove(`theme-${id}`));
    document.body.classList.add(`theme-${themeId}`);
    localStorage.setItem("pine_connector_theme", themeId);
    setActiveTheme(themeId);
    showToast(`Theme switched to ${THEMES.find(t => t.id === themeId).name}!`);
  };

  const renderDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : format(date, "dd MMM yyyy, hh:mm a");
  };

  // ── Support channel access checks ─────────────────────────────────────────
  const canEmail    = hasSupportChannel(user, "email");
  const canTelegram = hasSupportChannel(user, "telegram");
  const canPhone    = hasSupportChannel(user, "contact number");
  // Chatbot is always available

  // ── Subscription info ─────────────────────────────────────────────────────
  const isExpired = user?.service_end ? new Date() > new Date(user.service_end) : false;
  const daysLeft = user?.service_end
    ? Math.max(0, Math.ceil((new Date(user.service_end) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <div>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
      <div className="page-header">
        <div>
          <div className="page-title">Settings</div>
          <div className="page-sub">Manage your profile, appearance, and subscription</div>
        </div>
        <button className="btn btn-danger" onClick={handleLogout} style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
          <LogOut size={16} />
          Sign Out
        </button>
      </div>

      <div className="grid-2">
        {/* ── LEFT COLUMN ─────────────────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>

          {/* Profile Info */}
          <div className="card" style={{ padding: 24, marginBottom: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, borderBottom: "1px solid var(--border)", paddingBottom: "12px" }}>
              <User size={20} style={{ color: "var(--accent)" }} />
              <div style={{ fontWeight: 700, fontSize: 16 }}>Profile Information</div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
              <div style={{
                width: 60, height: 60, borderRadius: "50%",
                background: "linear-gradient(135deg, var(--accent), var(--accent-purple))",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 22, fontWeight: 800, color: "#000", boxShadow: "var(--glow)"
              }}>
                {(user?.name?.charAt(0) || "U").toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 18, color: "var(--text)" }}>{user?.name}</div>
                <div style={{ color: "var(--text2)", fontSize: "13px" }}>{user?.email}</div>
                <span className={`badge ${user?.role === "admin" ? "badge-buy" : "badge-pending"}`} style={{ marginTop: 6, fontSize: "10px" }}>
                  {user?.role === "admin" ? "Platform Admin" : `${user?.service_plan?.toUpperCase() || "DEMO"} Plan`}
                </span>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", background: "rgba(0,0,0,0.15)", borderRadius: "var(--radius)", padding: "20px", border: "1px solid var(--border)" }}>
              {[
                { label: "Active Plan",      value: user?.service_plan?.toUpperCase() || "DEMO" },
                { label: "Validity Status",  value: user?.role === "admin" ? "Lifetime Unlimited" : (isExpired ? "Expired" : "Active") },
                { label: "Expiration Date", value: renderDate(user?.service_end) },
                { label: "License IDs",     value: `${user?.license_ids?.length || 0} / ${user?.allowed_licenses || 1}` },
                { label: "MT5 / License",   value: `${user?.mt5_per_license_allowed || 1} connections` },
                { label: "TV Strategies",   value: `${user?.num_strategies_allowed || 0} included` },
                { label: "Support Tier",    value: (user?.support || "Chatbot").replace(/,/g, " · ") },
                { label: "Contact Number",  value: user?.kyc_details?.phone || "No plan purchased yet (Demo Plan)" },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ color: "var(--text2)", fontSize: 10, textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}>{label}</span>
                  <span style={{ fontSize: "13px", fontWeight: "800", color: "var(--text)" }}>{value}</span>
                </div>
              ))}
            </div>
            
            {/* Upgrade/Renew CTA when active plan is not admin */}
            {user?.role !== "admin" && (
              <button
                className="btn btn-primary"
                style={{ width: "100%", justifyContent: "center", gap: "8px", marginTop: "20px" }}
                onClick={() => navigate("/renew-plan")}
              >
                <ShieldCheck size={16} />
                {isExpired ? "Renew Subscription" : "View & Upgrade Plans"}
                <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>

        {/* ── RIGHT COLUMN ─────────────────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>

          {/* Security & Credentials */}
          <div className="card" style={{ padding: 24, marginBottom: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, borderBottom: "1px solid var(--border)", paddingBottom: "12px" }}>
              <Lock size={20} style={{ color: "var(--accent)" }} />
              <div style={{ fontWeight: 700, fontSize: 16 }}>Security &amp; Credentials</div>
            </div>

            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input className="form-input" value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-input" type="email" value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">New Password <span style={{ color: "var(--text2)", fontWeight: 500 }}>(leave blank to keep current)</span></label>
              <input className="form-input" type="password" placeholder="New secure password"
                value={form.password}
                onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input className="form-input" type="password" placeholder="Repeat new password"
                value={form.confirm}
                onChange={e => setForm(p => ({ ...p, confirm: e.target.value }))} />
            </div>
            {form.password && (
              <div className="form-group">
                <label className="form-label" style={{ color: "var(--accent)" }}>Current Password <span style={{ color: "var(--red)" }}>*</span></label>
                <input className="form-input" type="password" placeholder="Enter current password to verify"
                  value={form.oldPassword || ""}
                  onChange={e => setForm(p => ({ ...p, oldPassword: e.target.value }))}
                  required />
              </div>
            )}

            <button
              className="btn btn-primary"
              style={{ width: "100%", justifyContent: "center" }}
              onClick={handleUpdate}
              disabled={loading}
            >
              <Save size={16} />
              {loading ? "Saving changes..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}